import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BellRing, Send, SkipForward, Loader2 } from "lucide-react";
import { toast } from "sonner";

const OUTCOME = { Quoted: "bg-emerald-100 text-emerald-800", Claimed: "bg-sky-100 text-sky-800", Unsubscribed: "bg-red-50 text-red-700", "No response yet": "bg-slate-100 text-slate-600" };

function FollowupResults({ r }) {
  if (!r) return null;
  return (
    <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-3" data-testid="followup-results">
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Do the reminders pay off?</p>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Nudges sent", r.sent, "sent"], ["Then claimed", `${r.claimed} (${r.claim_rate}%)`, "claimed"], ["Then quoted", r.quoted, "quoted"]].map(([l, v, k]) => (
          <div key={k} data-testid={`followup-result-${k}`}><p className="text-xl font-display font-extrabold">{v}</p><p className="text-[11px] text-muted-foreground">{l}</p></div>
        ))}
      </div>
      {r.recent.length > 0 && (
        <ul className="space-y-1 max-h-40 overflow-y-auto text-xs" data-testid="followup-results-list">
          {r.recent.map((x, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span className="truncate">{x.business_name} <span className="text-muted-foreground">· nudged {ago(x.sent_at)}</span></span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 ${OUTCOME[x.outcome]}`}>{x.outcome}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ago = (iso) => `${Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000))} days ago`;

export function FollowupApprovals({ onChanged }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/followups").then((r) => { setData(r.data); setSel(r.data.items.map((i) => i.id)); }).catch(() => setData({ items: [], sent_last_30_days: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (path, msg) => {
    setBusy(true);
    try { const { data: r } = await api.post(`/admin/followups/${path}`, { ids: sel }); toast.success(msg(r)); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data) return null;
  const items = data.items;
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <Card className="p-4 space-y-3" data-testid="followup-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display font-bold flex items-center gap-2"><BellRing className="h-4 w-4 text-kinetic" /> Follow-up emails to approve
            {items.length > 0 && <span className="ml-1 rounded-full bg-kinetic text-white text-xs px-2 py-0.5" data-testid="followup-count">{items.length}</span>}</h3>
          <p className="text-xs text-muted-foreground">Unclaimed businesses that opened a lead 3+ days ago. Nothing is sent until you approve. Each business gets at most one nudge every 30 days. {data.sent_last_30_days} sent in the last 30 days.</p>
        </div>
        {items.length > 0 && (
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !sel.length} onClick={() => act("send", (r) => `${r.queued} follow-up email(s) sending`)} className="bg-kinetic hover:bg-kinetic-hover text-white gap-1.5" data-testid="followup-send-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Approve &amp; send ({sel.length})
            </Button>
            <Button size="sm" variant="outline" disabled={busy || !sel.length} onClick={() => act("skip", (r) => `${r.skipped} skipped`)} className="gap-1.5" data-testid="followup-skip-btn"><SkipForward className="h-3.5 w-3.5" /> Skip</Button>
          </div>
        )}
      </div>
      <FollowupResults r={data.results} />
      {items.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="followup-empty">No follow-ups waiting today.</p> : (
        <ul className="divide-y divide-border" data-testid="followup-list">
          {items.map((it) => (
            <li key={it.id} className="py-2 flex items-start gap-3 text-sm" data-testid={`followup-item-${it.id}`}>
              <input type="checkbox" className="mt-1" checked={sel.includes(it.id)} onChange={() => toggle(it.id)} data-testid={`followup-select-${it.id}`} />
              <div className="min-w-0">
                <p className="font-semibold">{it.listing?.business_name}</p>
                <p className="text-xs text-muted-foreground break-all">{it.listing?.email}{it.listing?.phone ? ` · ${it.listing.phone}` : ""} · {it.listing?.suburb} {it.listing?.state}</p>
                <p className="text-xs">Opened {it.leads_opened} lead{it.leads_opened === 1 ? "" : "s"}, last {ago(it.opened_at)}{it.job ? `: ${it.job.pickup_suburb} → ${it.job.dropoff_suburb}${it.job.status === "open" ? " (still open)" : ""}` : ""}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
